import { useCallback, useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import { getMe, type Me } from './api/me';
import { ApiError } from './api/httpClientFactory';
import { setSessionExpiredHandler } from './auth/authStore';
import { consumeCallback } from './auth/callback';
import { bootRefresh, clearSignInGuard, redirectToSignIn } from './auth/signIn';
import { SignOutButton } from './components/SignOutButton';
import { BootstrapError, getBootstrap, resolveBootstrap } from './config/bootstrap';
import { env } from './config/env';
import { COPY } from './copy';
import { WorkspaceRouter } from './router/WorkspaceRouter';
import { LoadingScreen } from './screens/LoadingScreen';
import { StatusScreen } from './screens/StatusScreen';
import { SessionProvider } from './session/SessionContext';
import { brandDiagnosticsEnabled, syncBrandTheme } from './theme/brandTheme';
import { useBrandTheme } from './theme/BrandThemeContext';
import { PLATFORM_DEFAULT_THEME } from './theme/platformDefaultTheme';

type ViewState =
  | { kind: 'loading' }
  | { kind: 'redirecting' }
  | { kind: 'bootstrap-error'; error: BootstrapError }
  | { kind: 'sign-in-stalled' }
  | { kind: 'api-error'; error: ApiError }
  | { kind: 'access-denied' }
  | { kind: 'ready'; me: Me; tenantName: string };

/**
 * The whole flow (CNPWKS-0001, CNPWKS-0003): bootstrap → one silent refresh (or sign-in redirect)
 * → /me and the router. A failed refresh leaves for sign-in; a retry that still fails after a
 * successful refresh is an error state and never a redirect (A4). The loop guard turns a repeat
 * redirect into 'stalled' (A5). A boot refresh that fails after returning on /auth/callback stalls
 * instead of redirecting (CNPWKS-0002).
 */
export function App() {
  const [view, setView] = useState<ViewState>({ kind: 'loading' });
  const started = useRef(false);
  const arrivedViaCallback = useRef(false);
  const { brandTheme, setBrandTheme } = useBrandTheme();
  // The painted brand version (null for the built-in theme), read by the theme sync (C5).
  const paintedVersion = useRef<string | null>(null);
  // Only the transition to ready waits on the theme: at most 1500 ms, none with a cache (C4).
  const brandReady = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    paintedVersion.current = brandTheme === PLATFORM_DEFAULT_THEME ? null : brandTheme.themeVersion;
  }, [brandTheme]);

  const leaveForSignIn = useCallback((force = false) => {
    const outcome = redirectToSignIn({ force });
    setView(outcome === 'redirected' ? { kind: 'redirecting' } : { kind: 'sign-in-stalled' });
  }, []);

  const loadMe = useCallback(async () => {
    setView({ kind: 'loading' });
    try {
      const me = await getMe();
      if (me.active === false) {
        setView({ kind: 'access-denied' });
        return;
      }
      await brandReady.current;
      clearSignInGuard();
      setView({ kind: 'ready', me, tenantName: getBootstrap().tenantName });
    } catch (e) {
      const error = e instanceof ApiError ? e : new ApiError('UNKNOWN', 0);
      // The factory already ran onSessionExpired → the handler → leaveForSignIn().
      if (error.code === 'SESSION_EXPIRED') return;
      if (error.status === 403 && error.code === 'PRINCIPAL_TYPE_UNSUPPORTED') {
        setView({ kind: 'access-denied' });
        return;
      }
      setView({ kind: 'api-error', error });
    }
  }, []);

  const start = useCallback(async () => {
    setView({ kind: 'loading' });
    try {
      await resolveBootstrap();
    } catch (e) {
      const error =
        e instanceof BootstrapError
          ? e
          : new BootstrapError(e instanceof Error ? e.message : String(e), null);
      setView({ kind: 'bootstrap-error', error });
      return;
    }
    // In parallel with the refresh; sign-in never waits on, retries or fails because of it.
    const bootstrap = getBootstrap();
    brandReady.current = syncBrandTheme(
      bootstrap.authOrigin,
      window.location.hostname,
      paintedVersion.current,
      setBrandTheme,
      { diagnostics: brandDiagnosticsEnabled(env.deployEnv, bootstrap.deployEnv) },
    );
    const ok = await bootRefresh();
    if (!ok) {
      // Back from sign-in with no session: stop here, never another redirect (CNPWKS-0002 B5).
      if (arrivedViaCallback.current) setView({ kind: 'sign-in-stalled' });
      else leaveForSignIn();
      return;
    }
    await loadMe();
  }, [leaveForSignIn, loadMe, setBrandTheme]);

  useEffect(() => {
    setSessionExpiredHandler(() => leaveForSignIn());
    return () => setSessionExpiredHandler(null);
  }, [leaveForSignIn]);

  // StrictMode double-invokes mount effects; the boot sequence is issued once.
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    arrivedViaCallback.current = consumeCallback();
    void start();
  }, [start]);

  switch (view.kind) {
    case 'loading':
      return <LoadingScreen message={COPY.loading} />;
    case 'redirecting':
      return <LoadingScreen message={COPY.redirecting} />;
    case 'bootstrap-error': {
      const { error } = view;
      const notRegistered = error.status === 404 || error.code === 'HOSTNAME_NOT_REGISTERED';
      return (
        <StatusScreen
          title={notRegistered ? COPY.notRegisteredTitle : COPY.bootstrapFailedTitle}
          body={notRegistered ? COPY.notRegisteredBody : COPY.bootstrapFailedBody}
          reference={bootstrapReference(error)}
          actions={
            <Button variant="contained" onClick={() => void start()}>
              {COPY.tryAgain}
            </Button>
          }
        />
      );
    }
    case 'sign-in-stalled':
      return (
        <StatusScreen
          title={COPY.signInStalledTitle}
          body={COPY.signInStalledBody}
          actions={
            <Button variant="contained" onClick={() => leaveForSignIn(true)}>
              {COPY.signInAgain}
            </Button>
          }
        />
      );
    case 'api-error':
      return (
        <StatusScreen
          title={COPY.apiErrorTitle}
          body={COPY.apiErrorBody}
          reference={view.error.requestId ?? view.error.code}
          actions={
            <>
              <Button variant="contained" onClick={() => void loadMe()}>
                {COPY.tryAgain}
              </Button>
              <SignOutButton variant="outlined" />
            </>
          }
        />
      );
    case 'access-denied':
      return (
        <StatusScreen
          title={COPY.accessDeniedTitle}
          body={COPY.accessDeniedBody}
          actions={<SignOutButton variant="contained" />}
        />
      );
    case 'ready':
      return (
        <SessionProvider
          value={{ me: view.me, tenantName: view.tenantName, deployEnv: env.deployEnv }}
        >
          <WorkspaceRouter />
        </SessionProvider>
      );
  }
}

function bootstrapReference(error: BootstrapError): string {
  return error.code ?? (error.status !== null ? `HTTP ${error.status}` : 'NETWORK');
}
